// app/javascript/controllers/card_selector_controller.js
import { Controller } from "@hotwired/stimulus"
import * as bootstrap from "bootstrap"
const LS_KEY = "invitation:favorites"    // stores array of slugs
const SYNC_KEY = "invitation:favorites_synced" // marks we've synced after login

export default class extends Controller {
    static values = {
        // Optional: set via data-card-selector-current-user-id-value on the parent container
        currentUserId: Number,
        profile: Number,
        charityId: Number,
        venueSlug: String,
        entrySource: String,
        guestEditingEnabled: Boolean
    }

    connect() {
        // On load, paint buttons based on favorites (from localStorage or server-render)
        const favs = this._getFavs()

        this.element.querySelectorAll("[data-card-selector-slug-value]").forEach(btn => {
            const slug = btn.dataset.cardSelectorSlugValue
            const isFav = favs.includes(slug)
        })

        // If user just logged in and we haven’t synced LS → server, do it once
        if (this.hasCurrentUserIdValue && !this._syncedOnce()) {
            this._syncToServer(favs)
        }
    }

    openEditor(e) {
        const slug = e.currentTarget.closest("[data-invitation-slug]")?.dataset.invitationSlug
        if (!slug) return

        const charityId = this.hasCharityIdValue ? this.charityIdValue : null
        const currentParams = new URLSearchParams(window.location.search)
        const backQs = window.location.search.length > 1 ? window.location.search.slice(1) : ""
        const exclusive = currentParams.get("exclusive") === "true"

        const params = new URLSearchParams()
        params.set("invitation", slug)

        if (this.hasVenueSlugValue && this.venueSlugValue) {
            params.set("venue", this.venueSlugValue)
        }

        if (this.hasEntrySourceValue && this.entrySourceValue) {
            params.set("entry_source", this.entrySourceValue)
        }

        if (charityId) {
            params.set("charity_id", charityId)
        }

        if (exclusive) {
            params.set("exclusive", "true")
        }

        if (backQs) {
            params.set("back_qs", backQs)
        }

        const guestEditable = e.currentTarget.closest("[data-invitation-slug]")?.dataset.guestEditable === "true"
        if ((this.hasCurrentUserIdValue && this.currentUserIdValue > 0) ||
            (this.guestEditingEnabledValue && guestEditable)) {
            window.location = `/party/editor?${params.toString()}`
        } else {
            // stash and open the login modal
            sessionStorage.setItem("pending_invitation_slug", slug)
            sessionStorage.removeItem("pending_ai_invitation")
            sessionStorage.removeItem("pending_ai_return_to")
            if (this.hasVenueSlugValue && this.venueSlugValue) {
                sessionStorage.setItem("pending_venue_slug", this.venueSlugValue)
            } else {
                sessionStorage.removeItem("pending_venue_slug")
            }
            if (this.hasEntrySourceValue && this.entrySourceValue) {
                sessionStorage.setItem("pending_entry_source", this.entrySourceValue)
            } else {
                sessionStorage.removeItem("pending_entry_source")
            }
            if (charityId) {
                sessionStorage.setItem("pending_charity_id", String(charityId))
            } else {
                sessionStorage.removeItem("pending_charity_id")
            }
            if (exclusive) {
                sessionStorage.setItem("pending_exclusive", "true")
            } else {
                sessionStorage.removeItem("pending_exclusive")
            }
            if (backQs) {
                sessionStorage.setItem("pending_back_qs", backQs)
            } else {
                sessionStorage.removeItem("pending_back_qs")
            }
            const modalEl = document.getElementById("loginModal")
            if (modalEl) {
                const bs = bootstrap.Modal.getOrCreateInstance(modalEl)
                bs.show()
            }
        }
    }

    stop(event) {
        event.stopPropagation()
        event.preventDefault()
    }

    openEditorByKey(event) {
        if (event.key !== "Enter" && event.key !== " ") return
        event.preventDefault()
        this.openEditor(event)
    }


    toggleFavorite(e) {
        e.preventDefault()
        const btn  = e.currentTarget
        const slug = btn.dataset.cardSelectorSlugValue
        if (!slug) return

        const favs = this._getFavs()
        const isFav = favs.includes(slug)

        if (isFav) {
            // remove
            this._setFavs(favs.filter(s => s !== slug))
            this._setHeartState(btn, false)
            this._maybeServer("DELETE", slug)
        } else {
            // add
            favs.push(slug)
            this._setFavs(favs)
            this._setHeartState(btn, true)
            this._maybeServer("POST", slug)
        }
    }

    // --- helpers ---------------------------------------------------------------

    _setHeartState(btn, active) {
        btn.classList.toggle("active", active)
        const icon = btn.querySelector("i")
        if (!icon) return
        icon.classList.toggle("bi-heart", !active)
        icon.classList.toggle("bi-heart-fill", active)
        icon.setAttribute("aria-hidden", "true")
        btn.setAttribute("aria-pressed", String(active))
        btn.setAttribute("aria-label", active ? "Remove from favorites" : "Add to favorites")
    }

    _getFavs() {
        try {
            return JSON.parse(localStorage.getItem(LS_KEY) || "[]")
        } catch(_) {
            return []
        }
    }
    _setFavs(arr) {
        localStorage.setItem(LS_KEY, JSON.stringify(Array.from(new Set(arr))))
    }

    _syncedOnce() {
        return localStorage.getItem(SYNC_KEY) === "1"
    }
    _markSynced() {
        localStorage.setItem(SYNC_KEY, "1")
    }

    _csrf() {
        const meta = document.querySelector('meta[name="csrf-token"]')
        return meta?.content
    }

    _maybeServer(method, slug) {
        if (!this.hasCurrentUserIdValue) return // anonymous; LS only
        const url = method === "POST" ? "/favorites" : `/favorites/${encodeURIComponent(slug)}`
        const body = method === "POST" ? JSON.stringify({ slug }) : undefined

        fetch(url, {
            method,
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": this._csrf()
            },
            body
        }).then((res) => {
            if (res.ok && this.hasProfileValue) {
                window.location.reload()
            } else {
                throw new Error("Delete failed")
            }
        }).catch(() => { /* network errors ignored; LS stays truthful */ })

    }

    _syncToServer(slugs) {
        if (!this.hasCurrentUserIdValue || slugs.length === 0) {
            this._markSynced()
            return
        }
        fetch("/favorites/sync", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": this._csrf()
            },
            body: JSON.stringify({ slugs })
        })
            .then(() => this._markSynced())
            .catch(() => { /* try again next page */ })
    }
}
